import React, { Component, lazy, Suspense } from 'react';
import { Header, toggleTheme, ThemeType, Loader } from '@scuf/common';
import { withAuthService, WithAuthServiceProps } from './components/hoc/withAuthService';
import { AuthenticatedUser } from './auth/models/AuthenticationModels';
import SidebarComponent from './components/Sidebar/Sidebar';
import routes from './routes';

import IdleTimer from 'react-idle-timer';
import { ToastContainer } from 'react-toastify';


import './App.scss';
import i18next from './i18next';
import {
  EUserPersonalisation,
  IUserAccountPreference,
  IUserAccountPreferences,
  IUserProfile,
  IUserSettingsData
} from './models';
import toasts from './modules/commonUtils/toasts';
import { LanguageCodes } from './locales/LanguageCodeObj';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { LDProps } from 'launchdarkly-react-client-sdk/lib/withLDConsumer';
import { RouteProps, withRouter, matchPath } from 'react-router-dom';
import { SITE_ROUTE_PATH } from './routes';
import OrganisationList from './modules/commonUtils/OrganisationList';
import { connect } from 'react-redux'
import { fetchBundleList, fetchOrganisationList, fetchUserList, IOrganisationState, setEnableRolePrivilegesForSIOrg, setOrganizationModalValue } from './modules/commonUtils/Redux/Global/globalShared';
import { bindActionCreators, compose, Dispatch } from 'redux';
import { OrgSelector, SelectedOrgSelector, UserSelector, selectedOrganizationModalValueSelector, enableRolePrivilegesForSIOrgSelector } from './modules/commonUtils/Redux/Global/selectors';
import { IOrganisation, IPermission } from './modules/onboarding/onboardingModels';
import GatewayOrganisationList from './modules/commonUtils/GatewayOrganisationList';
import GatewayIdleTime from './modules/commonUtils/GatewayIdleTime';
import OrganizationModal from './modules/commonUtils/OrganizationModal';
import { select } from 'redux-saga/effects';
import TermsAndConditions from './modules/onboarding/TermsAndConditions/TermsAndConditions';
import { UserProfileComponent } from '@bmsp/bmsp.webcomponent.user.management';
import AgreementModal from './modules/onboarding/TermsAndConditions/AgreementModal';
import { getFeatureFlagValuebasedOnSIOrg, getFeatureFlagValuebasedOnSIOrgWhitelist, isFeatureFlagEnabledForUser } from './modules/commonUtils/LDFlagUtils';
import { loadSecuredConfig } from './loadSecuredConfig';
import { authService } from './auth/services/authService';
import ForbiddenPage from './pages/Forbidden/Forbidden';

// Lazy load ChatBot to prevent errors if micro-frontend is unavailable
const ChatBotMicroFrontendPage = lazy(() => 
  import('./remoteComponents/ChatBot/ChatBotMicroFrontendPage')
    .catch(() => ({ default: () => <></> }))
);
interface IGatewayManagementValueProps {
  visible?: boolean;
  isApplied: boolean;
  privilegesLoaded?: boolean;
}

interface ILandingPageDashboardValueProps {
  visible?: boolean;
  isApplied: boolean;
  privilegesLoaded?: boolean;
}

interface IIntegrationMenuValueProps {
  visible?: boolean;
  isApplied: boolean;
  privilegesLoaded?: boolean;
}

interface ILicenseManagementValueProps {
  visible?: boolean;
  privilegesLoaded?: boolean;
  isApplied: boolean;
  hasFetchedLicenseData?: boolean;
}

interface IActivityLogValueProps {
  visible?: boolean;
  privilegesLoaded?: boolean;
  isApplied: boolean;
}

interface IProjectManagementTabValueProps {
  visible?: boolean;
  isApplied: boolean;
}

interface IAppState {
  user: AuthenticatedUser | null;
  userLock: boolean;
  theme: ThemeType;
  sideBarCollapsed: boolean;
  globalPermissionsLock: boolean;
  languageCode: string;
  enableSubscription: boolean;
  enableAssetModelingForSIOrg: boolean | undefined;
  newSideMenuBar: boolean;
  userProfile: IUserProfile | null;
  location: string;
  isSidebarMenu: boolean;
  gatewayManagementValues: IGatewayManagementValueProps;
  enableRolePrivilegesForSIOrg: boolean | undefined;
  landingPageDashboardValues: ILandingPageDashboardValueProps;
  organizationModalValue: boolean;
  userProfileSettingModalValue: boolean;
  isagreementModalOpen: boolean;
  agreementModalType: string;
  accountSettingsResponse?: IUserSettingsData | null;
  integrationMenuValues: IIntegrationMenuValueProps;
  licenseManagementValues: ILicenseManagementValueProps;
  canCreateUser: boolean;
  canEditUser: boolean;
  canDeleteUser: boolean;
  canCreateProject: boolean;
  canEditProject: boolean;
  canDeleteProject: boolean;
  canUpdateProjectStructure: boolean;
  canUpdateProjectDocument: boolean;
  canViewProjectDocument: boolean;
  highestRole: string;
  activityLogValues: IActivityLogValueProps;
  projectManagementTabValues: IProjectManagementTabValueProps;
}

interface IAppProps extends LDProps, RouteProps, WithAuthServiceProps {
  fetchOrganisationList: typeof fetchOrganisationList;
  fetchBundleList: typeof fetchBundleList;
  fetchUserList: typeof fetchUserList;
  userProfileDetails: IUserProfile | null;
  organisations: IOrganisation[] | null;
  selectedOrganisation: IOrganisation | null;
  history: any;
  organizationModalValue: boolean;
  setOrganizationModalValue: typeof setOrganizationModalValue;
  enableRolePrivilegesForSIOrg: boolean | undefined;
  setEnableRolePrivilegesForSIOrg: typeof setEnableRolePrivilegesForSIOrg;
}

export class App extends Component<IAppProps, IAppState> {
  private idleTimerRef = React.createRef<IdleTimer>();
  private technicianExternalLogoutTimer?: ReturnType<typeof setTimeout>;
  constructor(props: IAppProps) {
    super(props);
    this.state = {
      user: null,
      userLock: true,
      theme: ThemeType.Light,
      sideBarCollapsed: true,
      globalPermissionsLock: true,
      languageCode: LanguageCodes.enUS,
      enableSubscription: false,
      userProfile: null,
      location: '',
      isSidebarMenu: false,
      gatewayManagementValues: {
        visible: false,
        isApplied: false,
        privilegesLoaded: false
      },
      enableAssetModelingForSIOrg: undefined,
      enableRolePrivilegesForSIOrg: undefined,
      newSideMenuBar: false,
      landingPageDashboardValues: {
        visible: false,
        isApplied: false,
        privilegesLoaded: false
      },
      organizationModalValue: false,
      userProfileSettingModalValue: false,
      isagreementModalOpen: false,
      agreementModalType: '',
      accountSettingsResponse: null,
      integrationMenuValues: {
        visible: false,
        isApplied: false,
        privilegesLoaded: false
      },
      licenseManagementValues: {
        visible: false,
        privilegesLoaded: false,
        isApplied: false
      },
      canCreateUser: false,
      canEditUser: false,
      canDeleteUser: false,
      canCreateProject: false,
      canEditProject: false,
      canDeleteProject: false,
      canUpdateProjectStructure: false,
      canUpdateProjectDocument: false,
      canViewProjectDocument: false,
      highestRole: 'None',
      activityLogValues: {
        visible: false,
        privilegesLoaded: false,
        isApplied: false
      },
      projectManagementTabValues: {
        visible: false,
        isApplied: false
      }
    };
    this.getUserList = this.getUserList.bind(this);
    this.getSidebarRoutes = this.getSidebarRoutes.bind(this);
    this.renderSidebarComponent = this.renderSidebarComponent.bind(this);
    this.getShowGatewayManagementFlag = this.getShowGatewayManagementFlag.bind(this);
    this.getEnableBacsFlag = this.getEnableBacsFlag.bind(this);
    this.getShowIntegrationMenuFlag = this.getShowIntegrationMenuFlag.bind(this);
    this.getShowLicenseManagementFlag = this.getShowLicenseManagementFlag.bind(this);
  }

  getShowGatewayManagementFlag() {
    return this.state.user || false;
  }

  getShowIntegrationMenuFlag() {
    return this.state.user || false;
  }

  getShowLicenseManagementFlag() {
    return this.state.user || false;
  }

  getActivityLogGateState() {
    const isActivityLogFeatureEnabled = this.props.flags?.enableActivityLogForSi ?? false;
    const isOnActivityLogPage = this.props.location?.pathname === '/app/feature/activity-log';
    const permissionLoaded = this.state.activityLogValues.privilegesLoaded ?? false;
    const hasActivityPermission = this.state.activityLogValues.visible ?? false;
    const isActivityPermissionPending = !permissionLoaded;

    const isActivityLogEnabled =
      isActivityLogFeatureEnabled &&
      (hasActivityPermission || (isOnActivityLogPage && isActivityPermissionPending));

    return {
      isActivityLogFeatureEnabled,
      isOnActivityLogPage,
      permissionLoaded,
      hasActivityPermission,
      isActivityPermissionPending,
      isActivityLogEnabled
    };
  }

  getEnableBacsFlag() {
    return (
      getFeatureFlagValuebasedOnSIOrg(
        'enableBacsFlow',
        this.props.flags,
        this.props.selectedOrganisation,
        this.props.organisations
      ) ||
      (this.props.flags?.enableBacsFlowDistributor &&
        (this.props.selectedOrganisation?.orgType?.toLowerCase() === 'contractor' ||
          this.props.selectedOrganisation?.orgType?.toLowerCase() === 'distributor'))
    );
  }

  // Theme change for common library for the newly added Organization Management & Subscription Management
  themeChangeForgeCommon(currentTheme: string | null, isInitialLoading: boolean = false) {
    if (currentTheme === ThemeType.Light.toString() && !isInitialLoading) {
      window.document.documentElement.classList.remove('honeywell-light-theme', 'light');
      window.document.documentElement.classList.add('honeywell-dark-theme', 'dark');
    }
    else if (currentTheme === ThemeType.Light.toString() && isInitialLoading) {
      window.document.documentElement.classList.remove('honeywell-dark-theme', 'dark');
      window.document.documentElement.classList.add('honeywell-light-theme', 'light');
    }
    else if (currentTheme === ThemeType.Dark.toString() && isInitialLoading) {
      window.document.documentElement.classList.remove('honeywell-light-theme', 'light');
      window.document.documentElement.classList.add('honeywell-dark-theme', 'dark');
    }
    else {
      window.document.documentElement.classList.remove('honeywell-dark-theme', 'dark');
      window.document.documentElement.classList.add('honeywell-light-theme', 'light');
    }
  }

  componentDidUpdate(prevProps: any, prevState: IAppState) {
    if (!this.props.flags?.enableLoginForTitanUsers) {
      localStorage.removeItem('selectedLoginOption');
    }
    const newPathFound = this.props.location && prevProps &&
      this.props.location?.pathname !== prevProps.location?.pathname;
    if (newPathFound) {
      this.setState({ location: this.props.location?.pathname as string });
    }

    // Handle auth state changes - when loading completes, initialize user-dependent features
    const { authService } = this.props;
    const prevAuthService = prevProps.authService;
    
    // When auth loading completes, unlock the app and load user-specific data
    if (prevAuthService && prevAuthService.isLoading && !authService.isLoading && this.state.userLock) {
      const user = authService.user;
      if (user !== null) {
        this.getUserAccountSettings();
        this.getGatewayManagementPrivileges();
        this.getProjectManagementPrivileges();
        this.props.flags?.enableBacsFlow && this.getLandingPagPrivileges();
      }
      if (!user) {
        authService.signIn();
      }
      this.setState({ user: user, userLock: false });
      this.getGlobalPermissions(user);
    }

    if (this.getShowGatewayManagementFlag() && !this.state.gatewayManagementValues.isApplied) {
      this.getGatewayManagementPrivileges();
    }

    if (this.getEnableBacsFlag() && !this.state.landingPageDashboardValues.isApplied) {
      this.getLandingPagPrivileges();
    }

    if (this.getShowIntegrationMenuFlag() && !this.state.integrationMenuValues.isApplied) {
      this.getIntegrationMenuPrivileges();
    }

    const isLicensesCheckEnabled = this.props.flags?.isLicensesCheck === true;
    if (isLicensesCheckEnabled && this.getShowLicenseManagementFlag() && !this.state.licenseManagementValues.isApplied) {
      this.getLicenseManagementPrivileges();
    }

    if (this.state.user && !this.state.projectManagementTabValues.isApplied) {
      this.getProjectManagementTabVisibility();
    }

    // Activity log permissions - fetch only if FF is enabled
    const activityGateState = this.getActivityLogGateState();

    if (activityGateState.isActivityLogFeatureEnabled &&
        !this.state.activityLogValues.isApplied) {
      this.getActivityLogPermissions();
    }

    // Re-fetch license status when organization changes to ensure menu visibility is updated
    if (this.props.selectedOrganisation?.id !== prevProps.selectedOrganisation?.id) {
      const { isActivityLogFeatureEnabled } = activityGateState;

      // Reset isApplied to trigger re-fetch, but keep visible at current value
      // This keeps the route available while API call is in progress
      this.setState({ 
        licenseManagementValues: { 
          ...this.state.licenseManagementValues,
          isApplied: false 
        },
        projectManagementTabValues: {
          visible: false,
          isApplied: false,
        },
        activityLogValues: {
          visible: false,
          privilegesLoaded: false,
          // Re-fetch only when FF check passes.
          isApplied: !isActivityLogFeatureEnabled
        }
      });
      // Re-fetch licenses download/preview privileged users when org changes
      if (this.state.user) {
        const siOrgId = sessionStorage.getItem('selected-organization-id') ?? '';
        authService.checkLicensesPrivilege(this.state.user.profile?.email, this.state.user.profile?.sub, siOrgId);
      }
    }

    // Redirect from activity-log page when access conditions are not met.
    // Keep user on the same route while permission check is still in progress.
    if (activityGateState.isOnActivityLogPage) {
      const { isActivityLogFeatureEnabled, permissionLoaded, hasActivityPermission } = activityGateState;

      if (!isActivityLogFeatureEnabled || (permissionLoaded && !hasActivityPermission)) {
        this.props.history.replace('/app/feature/home');
      }
    }

    // Redirect from licenses page if feature flag is disabled or no licenses available
    // This check runs after getLicenseManagementPrivileges() is triggered to avoid race conditions
    const isOnLicensesPage = this.props.location?.pathname === '/app/feature/licenses';
    if (isOnLicensesPage) {
      const isFeatureEnabled = getFeatureFlagValuebasedOnSIOrgWhitelist(
        'enableArrLicenseFlowForSi',
        this.props.flags,
        this.props.selectedOrganisation,
        this.props.organisations
      );
      const licenseDataLoaded = this.state.licenseManagementValues.privilegesLoaded;
      const hasLicenses = this.state.licenseManagementValues.visible;
      
      // Redirect immediately if feature flag is disabled
      // Or redirect after data loads if no licenses are available
      if (!isFeatureEnabled || (licenseDataLoaded && !hasLicenses)) {
        this.props.history.replace('/app/feature/home');
      }
    }

    // TechnicianExternal users are not allowed in web app.
    if (
      this.state.highestRole === 'TechnicianExternal' &&
      prevState.highestRole !== 'TechnicianExternal' &&
      this.props.location?.pathname !== '/app/forbidden'
    ) {
      this.props.history.replace('/app/forbidden?reason=technicianExternal');
    }

    // Allow TechnicianExternal users to see forbidden page briefly, then logout.
    if (
      this.state.highestRole === 'TechnicianExternal' &&
      prevState.highestRole !== 'TechnicianExternal'
    ) {
      if (this.technicianExternalLogoutTimer) {
        clearTimeout(this.technicianExternalLogoutTimer);
      }
      this.technicianExternalLogoutTimer = setTimeout(() => {
        authService.signOut();
      }, 10000);
    }

    if (
      this.state.highestRole !== 'TechnicianExternal' &&
      prevState.highestRole === 'TechnicianExternal' &&
      this.technicianExternalLogoutTimer
    ) {
      clearTimeout(this.technicianExternalLogoutTimer);
      this.technicianExternalLogoutTimer = undefined;
    }
  }

  componentDidMount() {
    if (!this.props.flags?.enableLoginForTitanUsers) {
      localStorage.removeItem('selectedLoginOption');
    }
    window.addEventListener('message', this.handleIframeMessage);

    const { authService } = this.props;
    window.addEventListener('mousemove', authService.resetSessionTimer);
    window.addEventListener('keypress', authService.resetSessionTimer);

    if (this.props.location?.pathname !== "/app/feature/subscription"
      && this.state.isSidebarMenu === false) {
      this.setState({
        isSidebarMenu: true
      });
    }

    this.setState({ location: this.props.location?.pathname as string })
    this.props.fetchUserList();
    this.props.fetchOrganisationList();
    this.props.fetchBundleList();
    
    // Lock the app until authentication status is determined and user data is available
    const user = authService.user;
    
    if (!authService.isLoading) {
      if (user !== null) {
        if (user?.access_token) {
          loadSecuredConfig(user.access_token);
        }
        this.getUserAccountSettings();
        this.getGatewayManagementPrivileges();
        this.getProjectManagementPrivileges();
        this.getProjectManagementTabVisibility();
        this.props.flags?.enableBacsFlow && this.getLandingPagPrivileges();
        // Fetch licenses privileged users once on app load
        const siOrgId = sessionStorage.getItem('selected-organization-id') ?? '';
        authService.checkLicensesPrivilege(user.profile?.email, user.profile?.sub, siOrgId);
      }
      if (!user) {
        authService.signIn();
      }
      this.setState({ user: user, userLock: false });
      this.getGlobalPermissions(user);
    }

    // Set color style for app
    const theme = localStorage.getItem('COLOR_THEME');
    if (theme) {
      toggleTheme(parseInt(theme));
      this.setState({ theme: parseInt(theme) as ThemeType });
      this.themeChangeForgeCommon(theme, true); //default forge common theme change
    } else {
      this.setState({ theme: ThemeType.Dark });
      toggleTheme(ThemeType.Dark);
      localStorage.setItem('COLOR_THEME', ThemeType.Dark.toString());
      this.themeChangeForgeCommon('1'); //default forge common theme change
    }
  }

  componentWillUnmount() {
    if (this.technicianExternalLogoutTimer) {
      clearTimeout(this.technicianExternalLogoutTimer);
      this.technicianExternalLogoutTimer = undefined;
    }
    window.removeEventListener('message', this.handleIframeMessage);
    window.removeEventListener('mousemove', authService.resetSessionTimer);
    window.removeEventListener('keypress', authService.resetSessionTimer);
  }

  resetSessionTimer = () => {
    // Session timer reset logic
  }

  // Get Integration Menu Privileges
  getIntegrationMenuPrivileges = async () => {
    const { authService } = this.props;
    this.setState({ integrationMenuValues: { isApplied: true } });

    const scopeKey = 'Integration';
    const visibleKey = 'Integration_Menu_View';
    const apiUrl = `${authService.baseURL}/api/v1/users/me/user-all-privileges?scopes=${scopeKey}`;

    const userIntegrationMenuPrivileges = await authService.authFetch(apiUrl, { method: 'GET' });

    if (userIntegrationMenuPrivileges.status === 200) {
      const userPrivileges = await userIntegrationMenuPrivileges.json();

      this.setState({
        integrationMenuValues: {
          isApplied: true,
          privilegesLoaded: true,
          visible: (userPrivileges.length > 0 && userPrivileges.some((e: { name: string }) => e.name === visibleKey)) || false
        }
      });
    } else {
      this.setState({
        integrationMenuValues: {
          visible: false,
          isApplied: true,
          privilegesLoaded: true
        }
      });
    }
  };

  // Get License Management Status
  getLicenseManagementPrivileges = async () => {
    const { authService } = this.props;
    // Set isApplied immediately to prevent multiple concurrent calls
    this.setState({ 
      licenseManagementValues: {
        visible: this.state.licenseManagementValues.visible,
        privilegesLoaded: false,
        isApplied: true
      }
    });

    // Fetch LicenseManagement user-all-privileges
    const userAllPrivileges = await authService.fetchUserAllPrivileges('LicenseManagement');
    
    const siOrgId = sessionStorage.getItem('selected-organization-id') ?? '';
    
    // If no organization is selected, hide the menu
    if (!siOrgId) {
      this.setState({
        licenseManagementValues: {
          visible: false,
          privilegesLoaded: true,
          isApplied: true
        }
      });
      // Navigate away only if currently on licenses page
      if (this.props.location?.pathname === '/app/feature/licenses') {
        this.props.history.replace('/app/feature/home');
      }
      return;
    }

    try {
      // Check if user has LicenseManagement privileges from user-all-privileges API
      const hasPrivileges = userAllPrivileges.some(
        (p: IPermission) => p.name === 'LicenseManagement_Read'
      );
      let apiUrl = `${authService.baseURL}/api/v1/orgs/${siOrgId}/licenses/status-summary`;
      if(this.props.selectedOrganisation?.orgType?.toLowerCase() === 'customer') {
        apiUrl = `${authService.baseURL}/api/v1/orgs/${this.props.selectedOrganisation?.id}/licenses/status-summary?forCustomerOrg=true`;
      }

      const licenseStatusResponse = await authService.authFetch(apiUrl, { method: 'GET' });

      if (licenseStatusResponse.status === 200) {
        const licenseStatus = await licenseStatusResponse.json();

        // Show menu if user has privileges AND any of the total values is greater than 0
        const hasLicenses = (licenseStatus?.activeTotal > 0) || 
                           (licenseStatus?.inactiveTotal > 0) || 
                           (licenseStatus?.renewalRequiredTotal > 0);
        
        this.setState({
          licenseManagementValues: {
            privilegesLoaded: true,
            visible: hasPrivileges && hasLicenses,
            isApplied: true
          }
        });
      } else {
        this.setState({
          licenseManagementValues: {
            visible: false,
            privilegesLoaded: true,
            isApplied: true
          }
        });
      }
    } catch (error) {
      this.setState({
        licenseManagementValues: {
          visible: false,
          privilegesLoaded: true,
          isApplied: true
        }
      });
    }
  };

  getProjectManagementTabVisibility = async () => {
    const { authService, selectedOrganisation } = this.props;
    const selectedOrgId = selectedOrganisation?.id;
    const zuoraAccountNo = selectedOrganisation?.zuoraAccountNo;

    this.setState({
      projectManagementTabValues: {
        visible: false,
        isApplied: true
      }
    });

    if (!selectedOrgId || !zuoraAccountNo) {
      return;
    }

    try {
      const response = await authService.authFetch(
        `${authService.baseURL}/api/v4/subscriptions/${selectedOrgId}/zuoraAccountNumber/${zuoraAccountNo}/showTab`,
        { method: 'GET' }
      );

      if (response.status === 200) {
        const isTabEnabled = await response.json();
        this.setState({
          projectManagementTabValues: {
            visible: isTabEnabled === true,
            isApplied: true
          }
        });
      } else {
        this.setState({
          projectManagementTabValues: {
            visible: false,
            isApplied: true
          }
        });
      }
    } catch (error) {
      console.error('Failed to fetch project management tab visibility:', error);
      this.setState({
        projectManagementTabValues: {
          visible: false,
          isApplied: true
        }
      });
    }
  };

  // Get Project Management and User Management Privileges
  getProjectManagementPrivileges = async () => {
    try {
      const response = await authService.authFetch(
        `${authService.BASE_URL}/api/v1/users/me/user-all-privileges?scopes=UserManagement`,
        { method: 'GET' }
      );

      if (response.status === 200) {
        const privileges: Array<{ name: string }> = await response.json();
        const privilegeNames = privileges.map((p: { name: string }) => p.name);

        const hasPortfolioManager = privilegeNames.includes('UserManagement_UserRoles_Assign_PortfolioManager');
        const hasProjectManager = privilegeNames.includes('UserManagement_UserRoles_Assign_ProjectManager');
        const hasTechnician = privilegeNames.includes('UserManagement_UserRoles_Assign_Technician');
        const hasProjectSupervisorExternal = privilegeNames.includes('UserManagement_UserRoles_Assign_ProjectSupervisorExternal');
        const hasTechnicianExternal = privilegeNames.length === 0;

        let canCreateUser = false;
        let canEditUser = false;
        let canDeleteUser = false;
        let canCreateProject = false;
        let canEditProject = false;
        let canDeleteProject = false;
        let canUpdateProjectStructure = false;
        let canUpdateProjectDocument = false;
        let canViewProjectDocument = false;

        const highestRole = hasPortfolioManager ? 'PortfolioManager'
          : hasProjectManager ? 'ProjectManager'
          : hasTechnician ? 'Technician'
          : hasProjectSupervisorExternal ? 'ProjectSupervisorExternal'
          : hasTechnicianExternal ? 'TechnicianExternal'
          : 'None';
        
        switch (highestRole) {
          case 'PortfolioManager':
            canCreateUser = true; canEditUser = true; canDeleteUser = true;
            canCreateProject = true; canEditProject = true; canDeleteProject = true;
            canUpdateProjectStructure = true; canUpdateProjectDocument = true; canViewProjectDocument = true;
            break;
          case 'ProjectManager':
            canCreateUser = true; canEditUser = true; canDeleteUser = true;
            canCreateProject = true; canEditProject = true; canDeleteProject = false;
            canUpdateProjectStructure = true; canUpdateProjectDocument = true; canViewProjectDocument = true;
            break;
          case 'Technician':
            canCreateUser = false; canEditUser = true; canDeleteUser = false;
            canCreateProject = false; canEditProject = false; canDeleteProject = false;
            canUpdateProjectStructure = false; canUpdateProjectDocument = true; canViewProjectDocument = true;
            break;
          case 'ProjectSupervisorExternal':
            canCreateUser = true; canEditUser = true; canDeleteUser = true;
            canCreateProject = false; canEditProject = false; canDeleteProject = false;
            canUpdateProjectStructure = false; canUpdateProjectDocument = false; canViewProjectDocument = true;
            break;
          case 'TechnicianExternal':
            canCreateUser = false; canEditUser = false; canDeleteUser = false;
            canCreateProject = false; canEditProject = false; canDeleteProject = false;
            canUpdateProjectStructure = false; canUpdateProjectDocument = false; canViewProjectDocument = false;
            break;
          default:
            canCreateUser = false; canEditUser = false; canDeleteUser = false;
            canCreateProject = false; canEditProject = false; canDeleteProject = false;
            canUpdateProjectStructure = false; canUpdateProjectDocument = false; canViewProjectDocument = false;
            break;
        }
        
        this.setState({
          canCreateUser,
          canEditUser,
          canDeleteUser,
          canCreateProject,
          canEditProject,
          canDeleteProject,
          canUpdateProjectStructure,
          canUpdateProjectDocument,
          canViewProjectDocument,
          highestRole
        });
      }
    } catch (error) {
      // Keep defaults (all false)
    }
  };

  // Get Gateway Management Privileges
  getGatewayManagementPrivileges = async () => {
    const { authService } = this.props;
    this.setState({ gatewayManagementValues: { isApplied: true } });

    const scopeKey = 'GatewayManagement';
    const visibleKey = 'GatewayManagement_Can_View';
    const apiUrl = `${authService.baseURL}/api/v1/users/me/user-all-privileges?scopes=${scopeKey}`;

    const userGatewayManagementPrivileges = await authService.authFetch(apiUrl, { method: 'GET' });

    if (userGatewayManagementPrivileges.status === 200) {
      const userPrivileges = await userGatewayManagementPrivileges.json();

      this.setState({
        gatewayManagementValues: {
          isApplied: true,
          privilegesLoaded: true,
          visible: (userPrivileges.length > 0 && userPrivileges.some((e: { name: string }) => e.name === visibleKey)) || false
        }
      });
    } else {
      this.setState({
        gatewayManagementValues: {
          visible: false,
          isApplied: true,
          privilegesLoaded: true
        }
      });
    }
  };

  // Get Dashboard Privileges
  getLandingPagPrivileges = async () => {
    const { authService } = this.props;
    this.setState({ landingPageDashboardValues: { isApplied: true } });

    const scopeKey = 'OrgDashboard';
    const visibleKey = 'OrgDashboard_Data_Read';
    const apiUrl = `${authService.baseURL}/api/v1/users/me/user-all-privileges?scopes=${scopeKey}`;

    const userDashboardPrivileges = await authService.authFetch(apiUrl, { method: 'GET' });

    if (userDashboardPrivileges.status === 200) {
      const userPrivileges = await userDashboardPrivileges.json();
      this.setState({
        landingPageDashboardValues: {
          isApplied: true,
          privilegesLoaded: true,
          visible: (userPrivileges.length > 0 && userPrivileges.some((e: { name: string }) => e.name === visibleKey)) || false
        }
      });

    } else {
      this.setState({
        landingPageDashboardValues: {
          visible: false,
          isApplied: true,
          privilegesLoaded: true
        }
      });
    }
  };

  // Get Activity Log Permissions
  getActivityLogPermissions = async () => {
    this.setState({
      activityLogValues: {
        visible: this.state.activityLogValues.visible,
        privilegesLoaded: false,
        isApplied: true
      }
    });

    try {
      const userAllPrivileges = await authService.fetchUserAllPrivileges('ActivityLog');
      const hasActivityLogPermission = userAllPrivileges.some(
        (p: IPermission) => p.name === 'ActivityLog_Write'
      );

      this.setState({
        activityLogValues: {
          privilegesLoaded: true,
          visible: hasActivityLogPermission,
          isApplied: true
        }
      });
    } catch (error) {
      console.error('Failed to fetch activity log permissions:', error);
      this.setState({
        activityLogValues: {
          visible: false,
          privilegesLoaded: true,
          isApplied: true
        }
      });
    }
  };

  getUserAccountSettings = async () => {
    const { authService } = this.props;
    try {
      const getAccountSettings = authService.authFetch(
        `${authService.baseURL}/api/user-settings?allDetails=true`,
        {
          method: 'GET'
        }
      );

      const getUserGlobalPrivileges = authService.authFetch(
        `${authService.baseURL}/api/v1/users/me/privileges?scopes=SubscriptionManagement&scopes=AssetModeling`,
        {
          method: 'GET'
        }
      );

      const getDFUserPrivileges = authService.authFetch(
        `${authService.baseURL}/api/v1/users/me/user-all-privileges?scopes=SubscriptionManagement&scopes=AssetModeling`,
        {
          method: 'GET'
        }
      );

      const getOrganisations = authService.authFetch(
        `${authService.baseURL}/api/RootOrganisations/LCBS?fetchOnPrem=true`,
        {
          method: 'GET'
        }
      );

      const [accountSettings, userGlobalPrivileges, organisations, dFUserPrivileges] = await Promise.all([
        getAccountSettings,
        getUserGlobalPrivileges,
        getOrganisations,
        getDFUserPrivileges
      ]);

    if (accountSettings.status === 200) {
      const userSettings = (await accountSettings.json()) as IUserSettingsData;
      if (!this.props.flags?.enableBacsFlowLocalization) {
        this.setState({ languageCode: 'en-US' });
      } else {
        this.setState({
          accountSettingsResponse: userSettings
        });
        if (userSettings && userSettings.settings.length > 0) {
          let userAccountPreferences: IUserAccountPreferences =
            userSettings.settings[0].value;
          if (userAccountPreferences !== null && userAccountPreferences) {
            let lang = userAccountPreferences.prefs.find(
              elem => elem.name === EUserPersonalisation.language
            );
            if (lang && lang.value) {
              try {
                let languageCode = lang.value;
                let translation = require(`./locales/${lang.value}/translations`)
                  .translations[languageCode].translation;
                i18next.addResources(languageCode, 'translation', translation);
                i18next.loadResources();
                i18next.changeLanguage(languageCode);
                i18next.loadLanguages(languageCode);
                this.setState({ languageCode: lang.value });
              } catch (e) {
                console.error(i18next.t('languageNotSupported'));
              }
            }
          }
        }
      }
    } else {
      toasts.error(i18next.t('errorUserSettings'));
    }

    if (userGlobalPrivileges.status === 200) {
      const userPrivileges = await userGlobalPrivileges.json();
      if (
        userPrivileges.length > 0 &&
        userPrivileges
          .map((e: { name: string }) => e.name)
          .includes('SubscriptionManagement_SKU_Read')
      ) {
        this.setState({
          enableSubscription: true
        });
      } else {
        this.setState({
          enableSubscription: false
        });
      }

    } else {
      toasts.error(i18next.t('errorUserGlobalPrivileges'));
      this.setState({
        enableSubscription: false
      });
    }

    if (dFUserPrivileges.status === 200) {
      const userDfPrivileges = await dFUserPrivileges.json();
      if (
        userDfPrivileges.length > 0 &&
        userDfPrivileges
          .map((e: { name: string }) => e.name)
          .includes('AssetModeling_Read')
      ) {
        this.setState({
          enableRolePrivilegesForSIOrg: true
        });
        this.props.setEnableRolePrivilegesForSIOrg(true);
      } else {
        this.setState({
          enableRolePrivilegesForSIOrg: false
        });
        this.props.setEnableRolePrivilegesForSIOrg(false);
      }

      if ((this.props.flags?.enableBacsFlowCustomer && this.props.organisations?.some(
        org => org.orgType?.toLowerCase() === 'customer'
      ))) {
        if (
          userDfPrivileges.length > 0 &&
          userDfPrivileges
            .map((e: { name: string }) => e.name)
            .includes('SubscriptionManagement_SKU_Read')
        ) {
          this.setState({
            enableSubscription: true
          });
        } else {
          this.setState({
            enableSubscription: false
          });
        }
      }

    } else {
      toasts.error(i18next.t('errorUserDFPrivileges'));
      this.setState({
        enableRolePrivilegesForSIOrg: false
      });
      this.props.setEnableRolePrivilegesForSIOrg(false);
    }

    if (organisations.status === 200) {
      const orgDetails = await organisations.json();
      const formattedOrgList = orgDetails?.resources

      if (orgDetails && formattedOrgList) { // check if organization list are not null
        const isContainsSI = formattedOrgList.some((item: any) =>
          (item.zuoraAccountNo !== null && item.orgType?.toLowerCase() !== 'distributor') || (this.props.flags?.enableBacsFlowDistributor && item.orgType?.toLowerCase() === 'contractor')  // if orglist contains SI or Contractor then enable a asset modelling menu
        );
        this.setState({ enableAssetModelingForSIOrg: isContainsSI });
      }
      else {
        this.setState({ enableAssetModelingForSIOrg: false })
      }
    }
    else {
      this.setState({ enableAssetModelingForSIOrg: false })
    }


    if (this.state.isSidebarMenu === false) {
      this.setState({
        isSidebarMenu: true
      });
    }
    } catch (error) {
      console.error('getUserAccountSettings error:', error);
    }
  };

  getThemeString = () => {
    if (this.state.theme === ThemeType.Dark) {
      return i18next.t('lightTheme');
    } else {
      return i18next.t('darkTheme');
    }
  };

  getGlobalPermissions = (user: AuthenticatedUser | null) => {
    const { authService } = this.props;
    if (user) {
      // Use preferred_username as cache key first to match master behavior.
      const userName = user.profile?.preferred_username || user.email;
      const cached = userName
        ? sessionStorage.getItem(`globalPermissions:${userName}`)
        : null;
      if (cached) {
        this.setState({ globalPermissionsLock: false });
      }

      authService
        .loadGlobalPermissions()
        .then(() => {
          // Permissions loaded successfully
        })
        .catch((err) => {
          console.error('[App] Couldnt load global permissions:', err);
          toasts.error('Couldnt load permissions');
        })
        .finally(() => {
          this.setState({ globalPermissionsLock: false });
        });
    } else {
      this.setState({ globalPermissionsLock: false });
    }
  };

  handleLanguageChange = async (language: string) => {
    const { authService } = this.props;
    let currentSettings = this?.state?.accountSettingsResponse;

    // Find the language setting in the current settings and update its value
    let updatedPrefs = currentSettings?.settings[0].value.prefs.map((pref: IUserAccountPreference) => {
      if (pref.name === "language") {
        return { ...pref, value: language };
      }
      return pref;
    }) || []; // Default to an empty array if `prefs` is undefined
    let requestBody;
    if (currentSettings) {
      currentSettings.settings[0].value.prefs = updatedPrefs;
      requestBody = JSON.stringify({
        settings: [
          {
            id: currentSettings.settings[0].id,
            type: "genericPrefs",
            name: "UserPreferences",
            isEnabled: true,
            value: {
              prefs: updatedPrefs
            }
          }
        ]
      });
    }

    await authService
      .authFetch(`${authService.baseURL}/api/user-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: requestBody
      })
      .then(() => {
        let languageCode = language;
        let translation = require(`./locales/${languageCode}/translations`)
          .translations[languageCode].translation;
        i18next.addResources(languageCode, 'translation', translation);
        i18next.loadResources();
        i18next.changeLanguage(languageCode);
        i18next.loadLanguages(languageCode);
        this.setState({ languageCode: languageCode });
      })
      .catch(error => {
        console.log('Update language failed');
      });
  };

  handleThemeChange = () => {
    const currentTheme = localStorage.getItem('COLOR_THEME');
    if (currentTheme === ThemeType.Light.toString()) {
      localStorage.setItem('COLOR_THEME', ThemeType.Dark.toString());
      toggleTheme(ThemeType.Dark);
      this.setState({ theme: ThemeType.Dark });
    } else {
      localStorage.setItem('COLOR_THEME', ThemeType.Light.toString());
      toggleTheme(ThemeType.Light);
      this.setState({ theme: ThemeType.Light });
    }
    this.themeChangeForgeCommon(currentTheme);
  };

  async getUserList() {
    this.props.fetchUserList();
  }

  getUserName() {
    let firstName = '', lastName = '';

    if (this.state.user) {
      firstName = this.state.user.givenName || '';
      lastName = this.state.user.familyName || '';
    }

    return { firstName, lastName };
  }

  renderSidebarComponent() {
    let gatewayBool = false;
    let landingBool = false;
    if (this.getShowGatewayManagementFlag()) {
      gatewayBool = this.state.gatewayManagementValues.privilegesLoaded || false;
    } else {
      gatewayBool = true;
    }

    if (this.getEnableBacsFlag()) {
      landingBool = this.state.landingPageDashboardValues.privilegesLoaded || false;
    } else {
      landingBool = true;
    }
    if (this.state.isSidebarMenu && gatewayBool && landingBool) {
      const { isActivityLogEnabled } = this.getActivityLogGateState();

      return (
        <SidebarComponent
          user={this.state.user}
          routes={this.getSidebarRoutes()}
          collapsed={this.state.sideBarCollapsed}
          userProfileDetails={this.props.userProfileDetails}
          theme={this.state.theme}
          onThemeChange={this.handleThemeChange}
          onModalClose={this.handleUserProfileSettingModal}
          onOrganizationModalOpen={this.handlelOrganisationModal}
          parentState={this.state}
          newSideMenuBar={this.getFeatureFlagKeyValue('newSideMenuBar')}
          enableBacsFlow={this.getEnableBacsFlag()}
          activityLogVisible={isActivityLogEnabled}
        />
      );
    }
  }

  // getFeatureFlagKeyValue = (key: string) => {
  //   let featureKey = false
  //   const featureFlag = this.props.flags ? this.props.flags[key] : undefined;
  //   if (featureFlag && Array.isArray(featureFlag)) {
  //     featureKey = featureFlag.includes(this.state.user?.profile.sub);
  //     if (!featureKey) {
  //       featureKey = featureFlag.includes(this.props.userProfileDetails?.email);
  //     }
  //   }
  //   return featureKey
  // }

  // This function is for User-Level Filtering and not Org-Level
  getFeatureFlagKeyValue = (key: string) => {

    let featureKey = false;
    const featureFlag = this.props.flags ? this.props.flags[key] : undefined;

    if (featureFlag && Array.isArray(featureFlag)) {
      if (featureFlag.some(flag => flag.toLowerCase() === 'all')) {
        return true;
      }

      featureKey = featureFlag.includes(this.state.user?.id);

      if (!featureKey && this.props.userProfileDetails?.email) {
        const emailToCheck = this.props.userProfileDetails.email.toLowerCase();
        featureKey = featureFlag
          .map(email => email.toLowerCase())
          .includes(emailToCheck);
      }
    }
    return featureKey;
  }

  // getFeatureFlagValuebasedOnSIOrg = (key: string) => {
  //   let featureKey = false
  //   const featureFlag = this.props.flags ? this.props.flags[key] : undefined;
  //   const orgSelected = this.props?.selectedOrganisation;
  //   const defaultOrganization = this.props.organisations?.find(item => item.zuoraAccountNo !== null);
  //   const selectedOrganization = orgSelected ? orgSelected : defaultOrganization;

  //   if (selectedOrganization && featureFlag && Array.isArray(featureFlag)) {
  //     featureKey = featureFlag.some((flag: string) => (flag.toLocaleLowerCase() === 'all' || (flag.toLowerCase() === selectedOrganization?.id.toLowerCase() || flag.toLowerCase() === selectedOrganization?.name.toLowerCase())));
  //   }
  //   return featureKey
  // }


  getSidebarRoutes() {
    const { authService } = this.props;
    let isKey = false;
    let gatewayBool = false;
    let isContainSiOrg = false;
    let landingpageBool = false;
    let enableSupportIcon = false;
    const flagProps = this.props.flags;
    const isEnableBacsFlow = this.getEnableBacsFlag();
    const isDistributor = this.props.selectedOrganisation?.orgType?.toLowerCase() === 'distributor';
    const isCustomer = this.props.selectedOrganisation?.orgType?.toLowerCase() === 'customer';

    // const isNewUserManagementKey = getFeatureFlagValuebasedOnSIOrg('enableBacsFlow', this.props.flags, this.props.selectedOrganisation, this.props.organisations)
    // const isNewOrganisationKey = getFeatureFlagValuebasedOnSIOrg('enableBacsFlow', this.props.flags, this.props.selectedOrganisation, this.props.organisations)
    const loggedInUserDetails = this.state.user;
    let isEnableIntegrationMenu = false;
    let isEnableARRLicenseFlow = false;

    if (flagProps) {
      isKey = flagProps.subscriptionToggle && this.state.enableSubscription;
    }

    if (flagProps && this.state.enableAssetModelingForSIOrg !== undefined && this.state.enableRolePrivilegesForSIOrg !== undefined) {
      isContainSiOrg = (this.state.enableAssetModelingForSIOrg && this.state.enableRolePrivilegesForSIOrg);
    }

    if (this.getShowGatewayManagementFlag()) {
      gatewayBool = this.state.gatewayManagementValues.visible || false;
    }

    if ((isEnableBacsFlow) && this.state.landingPageDashboardValues.visible) {
      landingpageBool = true;
    } else {
      landingpageBool = false;
    }
    if (flagProps) {
      enableSupportIcon = flagProps.enableSupportIconInSidebar;
      isEnableIntegrationMenu = isFeatureFlagEnabledForUser('enableIntegrationMenu', this.props.flags, this.state.user?.id) && (this.state.integrationMenuValues.visible ?? false);
      // License management menu visibility is controlled by both feature flag AND API response
      isEnableARRLicenseFlow = getFeatureFlagValuebasedOnSIOrgWhitelist(
        'enableArrLicenseFlowForSi',
        this.props.flags,
        this.props.selectedOrganisation,
        this.props.organisations
      ) 
      && (this.state.licenseManagementValues.visible ?? false);
    }

    let isBmsAssistEnabled = isFeatureFlagEnabledForUser('enableBmsAiAssist', this.props.flags, this.state.user?.profile?.sub);

    let isBmsAssistProjectOsSubscriptionEnabled = isFeatureFlagEnabledForUser('bmsAssistProjectOsSubscription', this.props.flags, this.state.user?.profile?.email);
    let isBmsAssistProjectOsSubscriptionEnabledByOrgId = isFeatureFlagEnabledForUser('bmsAssistProjectOsSubscription', this.props.flags, this.props.selectedOrganisation?.id || '');
    const isProjectManagementTabApiEnabled = this.state.projectManagementTabValues.visible ?? false;

    const { isActivityLogEnabled } = this.getActivityLogGateState();

    const sidebarRoutes = routes(
      isKey,
      isContainSiOrg,
      gatewayBool,
      isEnableBacsFlow,
      landingpageBool,
      loggedInUserDetails,
      enableSupportIcon,
      isDistributor,
      isCustomer,
      isEnableIntegrationMenu,
      isEnableARRLicenseFlow,
      this.props.selectedOrganisation?.id || '',
      this.props.selectedOrganisation?.name || '',
      isBmsAssistEnabled,
      isBmsAssistProjectOsSubscriptionEnabled,
      isBmsAssistProjectOsSubscriptionEnabledByOrgId,
      isProjectManagementTabApiEnabled,
      this.state.canCreateUser,
      this.state.canEditUser,
      this.state.canDeleteUser,
      this.state.canCreateProject,
      this.state.canEditProject,
      this.state.canDeleteProject,
      this.state.canUpdateProjectStructure,
      this.state.canUpdateProjectDocument,
      this.state.canViewProjectDocument,
      this.state.highestRole,
      isActivityLogEnabled,
      this.state.theme,
      this.state.languageCode,
      this.props.authService.baseURL, 
      this.props.authService.authFetch, 
      this.props.authService.offering,
      this.props.authService.hasGlobalPermission
    );

    const isRestrictedSidebarRole =
      this.state.highestRole === 'ProjectManager' ||
      this.state.highestRole === 'ProjectSupervisorExternal';

    if (!isRestrictedSidebarRole) {
      return sidebarRoutes;
    }

    const allowedSidebarPaths = [
      '/feature/home',
      '/feature/project-management',
      '/feature/users'
    ];

    return sidebarRoutes.map(route => {
      if (!route.name || allowedSidebarPaths.includes(route.path)) {
        return route;
      }

      return {
        ...route,
        name: ''
      };
    });
  }

  handleIframeMessage = (event: MessageEvent) => {
    if (event.data.type === 'idleTimeoutReset') {
      if (this.idleTimerRef.current) {
        this.idleTimerRef.current.reset();
      }
    }
  }

  handlelOrganisationModal = () => {
    this.props.setOrganizationModalValue(!this.props.organizationModalValue);
  };

  onAgreementButtonClick = (type: string) => {
    this.setState({
      isagreementModalOpen: true,
      agreementModalType: type
    });
  };

  handleUserProfileSettingModal = () => {
    this.setState({
      userProfileSettingModalValue: !this.state.userProfileSettingModalValue
    });
  };

  /**
 * Determines if the landing page dashboard should be accessible.
 * @returns {boolean} - True if the conditions are met, otherwise false.
 */
  isLandingPageDashboardAccessible(): boolean {
    if (this.getEnableBacsFlag() && this.state.landingPageDashboardValues.visible) {
      return true;
    }
    return false;
  }

  /**
  * Determines if the customer organisation dropdown should be accessible.
  * @returns {boolean} - True if the conditions are met, otherwise false.
  */
  isCustomerOrgAccessible(): boolean {
    // const siOrgList = this.props?.organisations?.filter(organisation => (organisation.orgType === "SI")) || []
    //   return (
    //     siOrgList.length > 0 &&
    //     siOrgList.some(org =>
    //       this.props.flags?.enableBacsFlow?.includes(org.id) || this.props.flags?.enableBacsFlow?.includes(org.name)
    //     )
    //   );
    return (
      getFeatureFlagValuebasedOnSIOrg(
        'enableBacsFlow',
        this.props.flags,
        null,
        this.props.organisations
      ) ||
      (this.props.flags?.enableBacsFlowDistributor &&
        this.props.organisations?.some(
          org => org.orgType === 'Distributor' || org.orgType === 'Contractor'
        )) ||
      (this.props.flags?.enableBacsFlowCustomer && this.props.organisations?.some(
        org => org.orgType?.toLowerCase() === 'customer'
      ))
    );
  }

  getSubscriptionManagementSIOrgSelection = () => {
    let siOrgSelectionComponent = null;
    if (this.isCustomerOrgAccessible()) {
      siOrgSelectionComponent = <GatewayOrganisationList triggerComponent="Subscription Management" />;
    }
    else {
      siOrgSelectionComponent = <OrganisationList BASE_URL={this.props.authService.baseURL} OFFERING={this.props.authService.offering} />;
    }
    return siOrgSelectionComponent;
  }


  render() {
    const { firstName, lastName } = this.props.userProfileDetails ? { firstName: this.props.userProfileDetails.firstName, lastName: this.props.userProfileDetails.lastName } : this.getUserName();
    const isEnableBacsFlow = this.getEnableBacsFlag();
    // const isNewUserManagementKey = getFeatureFlagValuebasedOnSIOrg('enableBacsFlow', this.props.flags, this.props.selectedOrganisation, this.props.organisations);
    const isNewSideMenuBar = this.getFeatureFlagKeyValue('newSideMenuBar')

    return (
      <>
        <IdleTimer
          // Perform a logout after 65 minutes of idleness
          ref={this.idleTimerRef as any}
          timeout={1000 * 60 * 65}
          onIdle={() => this.props.authService.signOut()}
          debounce={250}
        />
        <ToastContainer
          hideProgressBar={false}
          closeOnClick={false}
          // closeButton={false}
          newestOnTop={true}
          position="top-right"
          // autoClose={false}
          autoClose={10000}
          draggable={false}
        // toastClassName="toast-notification-wrap"
        />
        {!this.state.globalPermissionsLock && !this.state.userLock ? (
          <>
            <div className={isEnableBacsFlow === true ? (this.state.theme === ThemeType.Dark ? `forgeHeader${this.state.sideBarCollapsed ? "" : "-exp"}` : `forgeHeaderLight${this.state.sideBarCollapsed ? "" : "-exp"}`) : ""}>
              <Header
                title={i18next.t('onboardingPortal')}
                className="header-shadowed"
                logoUrl={
                  this.state.theme === ThemeType.Dark
                    ? '/assets/honeywellWhiteLogo.svg'
                    : '/assets/honeywellBlackLogo.svg'
                }
                onMenuToggle={() =>
                  this.setState(prevState => ({
                    sideBarCollapsed: !prevState.sideBarCollapsed
                  }))
                }
              >
                {
                  this.state.user && isFeatureFlagEnabledForUser('enableChatbotForPartnerPortal', this.props.flags, this.state.user?.id) &&
                  <Suspense fallback={null}>
                    <ChatBotMicroFrontendPage
                      hostId='partner_portal'
                      organizationDetail={{
                        organizationId: this.props.selectedOrganisation ? this.props?.selectedOrganisation?.id : '',
                        organizationName: this.props.selectedOrganisation ? this.props.selectedOrganisation.name : ''
                      }}
                      userDetail={{
                        userId: this.state.user?.email || this.state.user?.id || '',
                        email: this.state.user?.email || ''
                      }}

                    />
                  </Suspense>
                }
                {
                  (this.state.location === '/app/feature/subscription') &&
                  this.getSubscriptionManagementSIOrgSelection()
                }
                {(this.state.location === '/app/feature/gateway-management') && <GatewayOrganisationList triggerComponent={"Gateway Management"} />}
                {(this.state.location === '/app/feature/gateway-management') && <GatewayIdleTime />}

                {(this.isCustomerOrgAccessible() && this.state.location === '/app/feature/home') && <GatewayOrganisationList triggerComponent={"Dashboard"} />}
                {(this.isCustomerOrgAccessible() && this.state.location === '/app/feature/home') && <GatewayIdleTime />}
                {(this.isCustomerOrgAccessible() && this.state.location === '/app/feature/integrations') && <GatewayIdleTime />}
                {this.props.organizationModalValue === true && (
                  <OrganizationModal
                    handlelOrganisationModal={this.handlelOrganisationModal}
                    loggedInUser={this.state.user}
                    onAgreementButtonClick={this.onAgreementButtonClick}
                  />
                )}
                {this.state.isagreementModalOpen && (
                  <AgreementModal
                    languageCode={this.state.languageCode}
                    title={
                      this.state.agreementModalType === 'EULA'
                        ? i18next.t('endUser')
                        : `Honeywell ${i18next.t('privacyPolicy')}`
                    }
                    type={this.state.agreementModalType}
                    onClose={(version: string) => {
                      console.log('version', version);
                      this.setState({ isagreementModalOpen: false });
                    }}
                  />
                )}
                {(this.state.location === '/app/feature/organisations' ||
                  matchPath(this.state.location, { path: SITE_ROUTE_PATH, exact: true })) &&
                  this.isCustomerOrgAccessible() && (
                    <GatewayOrganisationList triggerComponent="Customer Management" />
                  )}
                {this.state.location.startsWith('/app/feature/integrations') &&
                  this.isCustomerOrgAccessible() && (
                    <GatewayOrganisationList triggerComponent="Integration" />
                  )}
                {this.state.location === '/app/feature/users' && this.isCustomerOrgAccessible() && (
                  <GatewayOrganisationList triggerComponent="User Management" />
                )}
                  {this.state.location === '/app/feature/licenses' && this.isCustomerOrgAccessible() && (
                  <GatewayOrganisationList triggerComponent="License Management" />
                )}
                {this.state.location.startsWith('/app/feature/project-management') &&
                  this.isCustomerOrgAccessible() && (
                    <GatewayOrganisationList triggerComponent="Project Management" />
                  )}
                {this.state.location === '/app/feature/activity-log'  && (
                  <GatewayOrganisationList triggerComponent="Activity Log" />
                )}
                {this.state.userProfileSettingModalValue === true &&
                  this.props.userProfileDetails && (
                    <UserProfileComponent
                      data={this.props?.userProfileDetails}
                      isGetForm={true}
                      showEdit={false}
                      theme={this.state.theme}
                      onModalClose={this.handleUserProfileSettingModal}
                      onThemeChange={this.handleThemeChange}
                      isTitan={false}
                      i18Lng={i18next}
                      selectOrgId={this.props?.selectedOrganisation?.id || ''}
                      defaultLanguage={this.state.languageCode}
                      onLanguageChange={(language) => this.handleLanguageChange(language)}
                      disableLanguageUpdate={!this.props.flags?.enableBacsFlowLocalization}
                      featureFlags={this.props.flags}
                    />
                  )}
                {/* {isNewSideMenuBar && (<Header.Item><div></div></Header.Item>)} */}
                {!isEnableBacsFlow && (<Header.UserProfile
                  firstName={firstName}
                  lastName={lastName}
                >
                  {(!isEnableBacsFlow) && (
                    <Header.UserProfile.Item
                      onClick={() => {
                        this.handleThemeChange();
                      }}
                    >
                      {this.getThemeString()}
                    </Header.UserProfile.Item>
                  )}

                  {!this.state.user ? (
                    <Header.UserProfile.Item
                      onClick={() => {
                        this.props.authService.signIn();
                      }}
                    >
                      {i18next.t('login')}
                    </Header.UserProfile.Item>
                  ) : (
                    <>
                      {isEnableBacsFlow &&
                        <Header.UserProfile.Item
                          onClick={() => {
                            this.handlelOrganisationModal();
                          }}
                        >
                          {i18next.t('Organization')}
                        </Header.UserProfile.Item>}
                      <Header.UserProfile.Item
                        disabled={!isEnableBacsFlow}
                        onClick={() => {
                          this.handleUserProfileSettingModal();
                        }}>
                        {`${firstName} ${lastName}`}
                      </Header.UserProfile.Item>
                      <Header.UserProfile.Item
                        onClick={() => {
                          this.props.authService.signOut();
                        }}
                      >
                        {i18next.t('logout')}
                      </Header.UserProfile.Item>
                    </>
                  )}
                </Header.UserProfile>)}
              </Header>
            </div>

            {this.state.highestRole === 'TechnicianExternal'
              ? <ForbiddenPage />
              : this.renderSidebarComponent()}
            {this.props.flags?.enableNewTermsAndConditions && (
              <TermsAndConditions
                baseURL={this.props.authService.baseURL}
                offering={this.props.authService.offering}
                authFetch={this.props.authService.authFetch}
                userProfile={this.props.userProfileDetails}
              />
            )}
          </>
        ) : (
          <Loader text={i18next.t('loaderWelcomeMessage')} />
        )}
      </>
    );
  }
}

const mapStateToProps = (state: IOrganisationState) => {
  return {
    userProfileDetails: UserSelector(state),
    organisations: OrgSelector(state),
    selectedOrganisation: SelectedOrgSelector(state),
    organizationModalValue: selectedOrganizationModalValueSelector(state),
    enableRolePrivilegesForSIOrg: enableRolePrivilegesForSIOrgSelector(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      fetchUserList,
      fetchOrganisationList,
      fetchBundleList,
      setOrganizationModalValue,
      setEnableRolePrivilegesForSIOrg,
    },
    dispatch
  );

export default withRouter(withLDConsumer()(
  withAuthService(connect(mapStateToProps, mapDispatchToProps)(App))
));
